import React from 'react'

/**
 * A horizontal rule + title + optional description, used to group
 * content on the profile page. Looks like an editorial section break.
 *
 * Visual rules:
 *   - The title is set in Hanken Grotesk, bold (h2/h3).
 *   - A 1px hair-line `border-rule` separates sections vertically.
 *   - `emphasis` is kept for callers but no longer draws a red rail:
 *     red is reserved for flags (DESIGN.md).
 *   - `compact` tightens padding, title size, and list row rhythm for
 *     dense profile tabs.
 */
interface SectionProps {
  title: string
  description?: React.ReactNode
  emphasis?: boolean
  trailing?: React.ReactNode
  children: React.ReactNode
  className?: string
  id?: string
  /** Tighter vertical rhythm (profile tabs, long lists). */
  compact?: boolean
  /** Top hair-line between sections (default true). */
  borderTop?: boolean
}

const Section: React.FC<SectionProps> = ({
  title,
  description,
  trailing,
  children,
  className = '',
  id,
  compact = false,
  borderTop = true,
}) => {
  const blockPad = compact ? 'pt-4 pb-4' : 'pt-6 pb-8'
  const headerMb = compact ? 'mb-2' : 'mb-4'
  const titleClass = compact ? 'text-[17px] font-bold text-ink' : 'text-[22px] font-bold text-ink'
  const descClass = compact
    ? 'mt-0.5 text-xs text-ink-500 max-w-prose leading-snug'
    : 'mt-1 text-sm text-ink-500 max-w-prose'

  return (
    <section
      id={id}
      className={`${blockPad} ${borderTop ? 'border-t border-rule' : ''} ${className}`}
    >
      <header className={`flex flex-wrap items-center justify-between gap-x-6 gap-y-2 ${headerMb}`}>
        <div className="min-w-0">
          <h2 className={titleClass}>{title}</h2>
          {description && <p className={descClass}>{description}</p>}
        </div>
        {trailing && <div className="shrink-0 ml-auto">{trailing}</div>}
      </header>
      {compact ? <div className="[&_li]:py-2">{children}</div> : children}
    </section>
  )
}

export default Section
